/**
 * The handful of inline SVGs the P3 UI needs. LIGHT is a requirement: no icon
 * font, no icon package — each glyph is a single stroked path, currentColor,
 * aria-hidden (labels live next to them, never inside them).
 */
import type { ReactNode } from "react";

export interface IconProps {
  size?: number;
}

function Svg({ size = 16, children }: IconProps & { children: ReactNode }) {
  return (
    <svg
      className="icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

/** Travel (plane) — day-run travel legs, Travel Card. */
export function PlaneIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M10.5 13.5 4 11l1.5-1.5 6 .5 4.5-4.5a1.6 1.6 0 0 1 2.5 2.5L14 12.5l.5 6L13 20l-2.5-6.5Z" />
    </Svg>
  );
}

/** Ground travel (wheels) — train/car/van/boat legs. */
export function WheelsIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4 15V8a2 2 0 0 1 2-2h9l4 5v4h-2.2M8.8 15H13" />
      <circle cx="7" cy="16.5" r="1.8" />
      <circle cx="15" cy="16.5" r="1.8" />
    </Svg>
  );
}

/** Schedule (clock) — crew-logistics rows. */
export function ClockIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 8v4.2l2.8 1.6" />
    </Svg>
  );
}

/** Timetable (note) — public running order rows. */
export function NoteIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M9 18V6l9-2v12" />
      <circle cx="6.8" cy="18" r="2.2" />
      <circle cx="15.8" cy="16" r="2.2" />
    </Svg>
  );
}

/** Hotel (bed) — Today's "tonight" line. */
export function BedIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M3 18v-8m0 5h18m0 3v-5a3 3 0 0 0-3-3H9v5" />
      <circle cx="6.5" cy="12" r="1.4" />
    </Svg>
  );
}

/** Search (magnifier) — the phone header affordance for the palette. */
export function SearchIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="11" cy="11" r="6.5" />
      <path d="m19.5 19.5-3.8-3.8" />
    </Svg>
  );
}

/** People — the Rolodex (cross-gig people memory) header affordance. */
export function PeopleIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5.5 19.5c.8-3.2 3.4-5 6.5-5s5.7 1.8 6.5 5" />
    </Svg>
  );
}

/** Folder — the Files vault (app-wide file browser) header affordance. */
export function FolderIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M3.5 6.5a1.5 1.5 0 0 1 1.5-1.5h4l2 2.5h8a1.5 1.5 0 0 1 1.5 1.5v9a1.5 1.5 0 0 1-1.5 1.5H5a1.5 1.5 0 0 1-1.5-1.5z" />
    </Svg>
  );
}
